import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TestPipeline from '../components/test/TestPipeline';
import { DEMO_QUESTIONNAIRE_PAPERS } from '../data/demoTestPaper';
import {
  mergeAbilityPapersWithApi,
  getCompletedAbilitySlugs
} from '../data/abilityPapers';
import { getAbilitySuiteAttemptId } from '../utils/abilitySuiteAttempt';
import { fetchTestPapers, fetchAbilitySubmissions } from '../services/testService';

function PersonalityTest() {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const autoOpenTutorial = searchParams.get('tutorial') === '1';
  const [papers, setPapers] = useState([]);
  const [completedSlugs, setCompletedSlugs] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [usedDemoFallback, setUsedDemoFallback] = useState(false);

  const refreshCompletions = React.useCallback(async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const attemptId = getAbilitySuiteAttemptId();
      const submissions = await fetchAbilitySubmissions(attemptId || undefined);
      setCompletedSlugs(getCompletedAbilitySlugs(submissions, attemptId));
    } catch {
      /* keep previous */
    }
  }, []);

  const clearCompletions = React.useCallback(() => {
    setCompletedSlugs(new Set());
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadError(null);
      try {
        let apiPapers = [];
        try {
          apiPapers = await fetchTestPapers('ability');
        } catch {
          apiPapers = [];
        }

        if (cancelled) return;

        const merged = mergeAbilityPapersWithApi(apiPapers);
        setPapers(merged);
        setUsedDemoFallback(!apiPapers.length);

        if (localStorage.getItem('token')) {
          try {
            const attemptId = getAbilitySuiteAttemptId();
            const submissions = await fetchAbilitySubmissions(attemptId || undefined);
            if (!cancelled) {
              setCompletedSlugs(getCompletedAbilitySlugs(submissions, attemptId));
            }
          } catch {
            if (!cancelled) setCompletedSlugs(new Set());
          }
        }
      } catch (e) {
        if (!cancelled) {
          setPapers(DEMO_QUESTIONNAIRE_PAPERS);
          setUsedDemoFallback(true);
          setLoadError(t('test.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  if (loading) {
    return (
      <div className="bg-canvas p-8 min-h-[50vh] flex items-center justify-center">
        <p className="text-ink-muted">{t('test.loadingPapers')}</p>
      </div>
    );
  }

  return (
    <div>
      {loadError && (
        <div className="max-w-3xl mx-auto px-4 pt-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-900 text-sm">
            {loadError}
          </div>
        </div>
      )}
      <TestPipeline
        papers={papers}
        completedSlugs={completedSlugs}
        onRefreshCompletions={refreshCompletions}
        onClearCompletions={clearCompletions}
        usedDemoFallback={usedDemoFallback}
        autoOpenTutorial={autoOpenTutorial}
      />
    </div>
  );
}

export default PersonalityTest;
